import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { useSnackbar } from "notistack";
import Button1 from "../../../components/form/Button1";
import Modal1 from "../../../components/DIsplay/Modal/Modal1";
import TextInput from "../../../components/form/TextInput";
import Checkbox from "../../../components/form/Checkbox";
import AgentSearchInput from "../../../components/form/AgentSearchInput";
import CopyButton from "../../../components/mini/CopyButton";
import useAccount from "../../../hooks/useAccount";
import getAPIKeys from "../../../controllers/settings/APIKeys/getAPIKeys";
import createAPIKey from "../../../controllers/settings/APIKeys/createAPIKey";
import createAPIKeyByAdmin from "../../../controllers/settings/APIKeys/createAPIKeyByAdmin";
import deleteAPIKey from "../../../controllers/settings/APIKeys/deleteAPIKey";
import getAPIKeyToken from "../../../controllers/settings/APIKeys/getAPIKeyToken";

const PAGE_SIZE = 10;

function unwrapCreatedKey(payload) {
  if (!payload) return null;
  if (payload.clientId) return payload;
  if (payload.data?.clientId) return payload.data;
  if (payload.apiKey?.clientId) return payload.apiKey;
  return payload;
}

function unwrapToken(payload) {
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  return (
    payload.token ||
    payload.accessToken ||
    payload.data?.token ||
    payload.data?.accessToken ||
    ""
  );
}

export function AccessKeys({ hideTabs = false }) {
  const { enqueueSnackbar } = useSnackbar();
  const { checkPermission } = useAccount();
  const { user } = useSelector((state) => state.user.userData);
  const isAdmin = user?.userType === "Admin";

  const [keys, setKeys] = useState([]);
  const [meta, setMeta] = useState({ limit: PAGE_SIZE, offset: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    scope: "Full",
    widgetEnabled: true,
  });
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [createdKey, setCreatedKey] = useState(null);
  const [createdOpen, setCreatedOpen] = useState(false);

  const [tokenOpen, setTokenOpen] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenForm, setTokenForm] = useState({
    clientId: "",
    clientSecret: "",
  });
  const [tokenValue, setTokenValue] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    load();
  }, [offset]);

  async function load() {
    setLoading(true);
    const res = await getAPIKeys({ limit: PAGE_SIZE, offset });
    setLoading(false);
    if (res.return) {
      setKeys(res.data || []);
      setMeta(res.meta || { limit: PAGE_SIZE, offset, total: 0 });
    } else {
      enqueueSnackbar(res.msg || "Failed to load API keys", {
        variant: "error",
      });
    }
  }

  function resetCreateForm() {
    setForm({ name: "", scope: "Full", widgetEnabled: true });
    setSelectedAgent(null);
  }

  async function handleCreate(ev) {
    ev.preventDefault();
    if (!form.name.trim()) {
      enqueueSnackbar("Token name is required", { variant: "warning" });
      return;
    }
    if (isAdmin && selectedAgent && !selectedAgent?._id) {
      enqueueSnackbar("Select a valid agent account", { variant: "warning" });
      return;
    }

    const payload = {
      name: form.name.trim(),
      scope: form.scope,
      widgetEnabled: Boolean(form.widgetEnabled),
    };

    setCreating(true);
    const res =
      isAdmin && selectedAgent?._id
        ? await createAPIKeyByAdmin({ ...payload, account: selectedAgent._id })
        : await createAPIKey(payload);
    setCreating(false);

    if (res.return) {
      enqueueSnackbar("API key created successfully", { variant: "success" });
      setCreateOpen(false);
      resetCreateForm();
      const key = unwrapCreatedKey(res.data);
      if (key?.clientId) {
        setCreatedKey(key);
        setCreatedOpen(true);
      }
      load();
    } else {
      enqueueSnackbar(res.msg || "Failed creating key", { variant: "error" });
    }
  }

  async function handleDelete() {
    if (!deleteTarget?.clientId) return;
    setDeleting(true);
    const res = await deleteAPIKey(deleteTarget.clientId);
    setDeleting(false);
    if (res.return) {
      enqueueSnackbar("API key removed", { variant: "success" });
      setDeleteTarget(null);
      load();
    } else {
      enqueueSnackbar(res.msg || "Failed to delete API key", {
        variant: "error",
      });
    }
  }

  function openTokenModal(key) {
    setTokenValue("");
    setTokenForm({
      clientId: key?.clientId || createdKey?.clientId || "",
      clientSecret:
        createdKey?.clientId === key?.clientId
          ? createdKey?.clientSecret || ""
          : "",
    });
    setTokenOpen(true);
  }

  async function handleGetToken(ev) {
    ev.preventDefault();
    if (!tokenForm.clientId || !tokenForm.clientSecret) {
      enqueueSnackbar("Client ID and Client Secret are required", {
        variant: "warning",
      });
      return;
    }

    setTokenLoading(true);
    const res = await getAPIKeyToken({
      clientId: tokenForm.clientId.trim(),
      clientSecret: tokenForm.clientSecret.trim(),
    });
    setTokenLoading(false);

    if (res.return) {
      const token = unwrapToken(res.data);
      setTokenValue(token || JSON.stringify(res.data || {}, null, 2));
      enqueueSnackbar("Token generated", { variant: "success" });
    } else {
      enqueueSnackbar(res.msg || "Failed to get token", { variant: "error" });
    }
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / PAGE_SIZE));
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < (meta.total || 0);

  return (
    <div className="flex flex-col gap-4 !text-primary/60 flex-1 h-full self-stretch">
      <div className="flex gap-4 justify-between items-center flex-wrap">
        {!hideTabs ? (
          <div className="flex gap-2 self-start">
            <Link className="btn" to="?view=accessKeys">
              Access Keys
            </Link>
            {checkPermission("read:webhook") ? (
              <Link className="btn-theme-light" to="?view=webhook">
                Web Hooks
              </Link>
            ) : null}
          </div>
        ) : (
          <div>
            <h5 className="text-[#1A1A1A]">API Keys</h5>
            <p className="text-sm">
              Create, view, and revoke API keys for this account
            </p>
          </div>
        )}
        <div className="flex gap-2">
          <Button1
            variant="outlined"
            className="!w-auto"
            onClick={() => openTokenModal()}
          >
            Get Token
          </Button1>
          <Button1 className="!w-auto" onClick={() => setCreateOpen(true)}>
            Create API Key
          </Button1>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scope
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Widget
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="animate-pulse">
                    {Array.from({ length: 6 }).map((__, cell) => (
                      <td key={cell} className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : keys.length ? (
                keys.map((key) => (
                  <tr key={key._id || key.clientId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {key.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span
                          id={`clientId-${key.clientId}`}
                          className="font-mono text-xs max-w-[160px] truncate block"
                          title={key.clientId}
                        >
                          {key.clientId}
                        </span>
                        <CopyButton id={`clientId-${key.clientId}`} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {key.scope || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {key.widgetEnabled == null
                        ? "—"
                        : key.widgetEnabled
                          ? "Enabled"
                          : "Disabled"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {key.createdAt
                        ? moment(key.createdAt).format("DD MMM YYYY, HH:mm")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-primary1 hover:underline"
                          onClick={() => openTokenModal(key)}
                        >
                          Get token
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline"
                          onClick={() => setDeleteTarget(key)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-sm text-gray-500"
                  >
                    You don't have any API keys yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
          <span>
            {meta.total
              ? `Showing ${offset + 1}–${Math.min(
                  offset + PAGE_SIZE,
                  meta.total,
                )} of ${meta.total}`
              : "No records"}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canPrev || loading}
              onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
              className="px-3 py-1 rounded border disabled:opacity-40"
            >
              Previous
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={!canNext || loading}
              onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
              className="px-3 py-1 rounded border disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <Modal1 open={createOpen} setOpen={setCreateOpen}>
        <form
          onSubmit={handleCreate}
          className="p-4 flex flex-col gap-5 max-w-[640px] w-[90vw]"
        >
          <h5>Create an API key</h5>
          {isAdmin ? (
            <div className="flex flex-col gap-2">
              <span className="text-sm">Create for agent (optional)</span>
              <AgentSearchInput
                selectedAgent={selectedAgent}
                onChange={setSelectedAgent}
                label="Agent account"
              />
              {selectedAgent?._id ? (
                <p className="text-xs">
                  This key will be created as admin for{" "}
                  <b>
                    {selectedAgent?.firstName} {selectedAgent?.lastName}
                  </b>
                </p>
              ) : null}
            </div>
          ) : null}
          <TextInput
            value={form.name}
            onChange={(ev) => setForm({ ...form, name: ev.target.value })}
            label="Token name"
            placeholder="e.g Miles token"
          />
          <div className="flex flex-col gap-2">
            <span>Scope</span>
            <label className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 cursor-pointer">
              <input
                type="radio"
                name="scope"
                value="Full"
                checked={form.scope === "Full"}
                onChange={() => setForm({ ...form, scope: "Full" })}
              />
              <div>
                <h5>Read and write</h5>
                <p className="text-sm">
                  Can read and modify any resource
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 cursor-pointer">
              <input
                type="radio"
                name="scope"
                value="ReadOnly"
                checked={form.scope === "ReadOnly"}
                onChange={() => setForm({ ...form, scope: "ReadOnly" })}
              />
              <div>
                <h5>Read only</h5>
                <p className="text-sm">Can read but not modify any resource</p>
              </div>
            </label>
          </div>
          <Checkbox
            checked={form.widgetEnabled}
            onChange={(ev) =>
              setForm({ ...form, widgetEnabled: ev.target.checked })
            }
          >
            Enable widget
          </Checkbox>
          <div className="flex gap-2">
            <Button1
              className="!w-[20%]"
              variant="text"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button1>
            <Button1 type="submit" className="!w-auto flex-1" loading={creating}>
              {isAdmin && selectedAgent?._id
                ? "Create as Admin"
                : "Create API Key"}
            </Button1>
          </div>
        </form>
      </Modal1>

      <Modal1 open={createdOpen} setOpen={setCreatedOpen}>
        <div className="p-4 flex flex-col gap-4 max-w-[640px] w-[90vw]">
          <h5>API key created</h5>
          <p className="text-sm">
            Copy the client secret now. You may not be able to see the original
            value again.
          </p>
          <div className="flex flex-col gap-2">
            <b>Client ID</b>
            <div className="flex gap-2 justify-between items-start">
              <p id="created-clientId" className="break-all font-mono text-xs">
                {createdKey?.clientId}
              </p>
              <CopyButton id="created-clientId" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <b>Client Secret</b>
            <div className="flex gap-2 justify-between items-start">
              <p
                id="created-clientSecret"
                className="break-all font-mono text-xs"
              >
                {createdKey?.clientSecret}
              </p>
              <CopyButton id="created-clientSecret" />
            </div>
          </div>
          <Button1
            className="!w-auto self-end"
            onClick={() => {
              setCreatedOpen(false);
              openTokenModal(createdKey);
            }}
          >
            Get token
          </Button1>
        </div>
      </Modal1>

      <Modal1 open={tokenOpen} setOpen={setTokenOpen}>
        <form
          onSubmit={handleGetToken}
          className="p-4 flex flex-col gap-4 max-w-[640px] w-[90vw]"
        >
          <h5>Get API token</h5>
          <TextInput
            value={tokenForm.clientId}
            onChange={(ev) =>
              setTokenForm({ ...tokenForm, clientId: ev.target.value })
            }
            label="Client ID"
          />
          <TextInput
            value={tokenForm.clientSecret}
            onChange={(ev) =>
              setTokenForm({ ...tokenForm, clientSecret: ev.target.value })
            }
            label="Client Secret"
          />
          {tokenValue ? (
            <div className="flex flex-col gap-2">
              <b>Token</b>
              <div className="flex gap-2 justify-between items-start">
                <p id="api-token-value" className="break-all font-mono text-xs">
                  {tokenValue}
                </p>
                <CopyButton id="api-token-value" />
              </div>
            </div>
          ) : null}
          <div className="flex gap-2">
            <Button1
              className="!w-[20%]"
              variant="text"
              onClick={() => setTokenOpen(false)}
            >
              Close
            </Button1>
            <Button1
              type="submit"
              className="!w-auto flex-1"
              loading={tokenLoading}
            >
              Get Token
            </Button1>
          </div>
        </form>
      </Modal1>

      <Modal1 open={Boolean(deleteTarget)} setOpen={() => setDeleteTarget(null)}>
        <div className="card flex flex-col items-center gap-4 p-4 max-w-[480px]">
          <h4>Delete API Key</h4>
          <p className="text-center">
            Are you sure you want to delete this access token?
            <br />
            You will not be able to undo this action.
          </p>
          <div className="flex gap-2 w-full">
            <Button1
              className="bg-theme-light !w-auto flex-1"
              variant="text"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button1>
            <Button1
              loading={deleting}
              className="!bg-red-500 !text-white !flex-1"
              onClick={handleDelete}
            >
              Delete
            </Button1>
          </div>
        </div>
      </Modal1>
    </div>
  );
}

export default function ApiKeySetting() {
  return <AccessKeys hideTabs />;
}
